import { useState } from 'react';
import { useLocation } from 'react-router';
import { PerformanceAlerts24 } from 'ui/icons';
import { CampaignState, CampaignType, MessageMedium } from 'modules/domains/campaign';
import { AlertHistoryFooter } from './AlertHistoryFooter';
import { AlertCampaign } from './AlertCampaign';
import { AlertHistory } from './AlertHistory';
import { CampaignAlert } from '../../hooks';
import {
  AlertsHistorySlideOverHeader,
  SlideOverHeaderTitle,
  StyledSlideOver,
} from './AlertHistorySlideOver.styled';
import { mappingByPageAndSource } from './utils';
import { CampaignAlertHistorySource } from './types';

export const ALERTS_HISTORY_SLIDE_OVER_ID = 'alerts-history-slide-over';

type AlertsHistorySlideOverProps = {
  campaignName: string;
  campaignId: number;
  templateId: number;
  campaignType: CampaignType;
  messageMedium: MessageMedium;
  campaignState: CampaignState;
  campaignAlertsEnabled: boolean;
  onClose: () => void;
  onEditAlerts: () => void;
  source: CampaignAlertHistorySource;

  /**
   * This is used to set the default selected alert when opening the slide over.
   *
   * NOTE: changing this prop will not change the selected alert in the slide over.
   * It is only used to set the initial selected alert when opening the slide over.
   */
  defaultSelectedAlert?: CampaignAlert | null;
};

export const AlertsHistorySlideOver = ({
  campaignName,
  campaignId,
  templateId,
  campaignType,
  messageMedium,
  campaignState,
  campaignAlertsEnabled,
  onClose,
  source,
  defaultSelectedAlert = null,
}: AlertsHistorySlideOverProps) => {
  const { pathname } = useLocation();
  const [selectedAlert, setSelectedAlert] = useState<CampaignAlert | null>(defaultSelectedAlert);

  const buttonContent = mappingByPageAndSource({
    campaignId,
    selectedAlert,
    pathname,
    source,
    campaignState,
  });

  return (
    <StyledSlideOver
      isOpen
      withPortal
      id={ALERTS_HISTORY_SLIDE_OVER_ID}
      data-test={ALERTS_HISTORY_SLIDE_OVER_ID}
      onClose={onClose}
      onClickOutside={onClose}
      $withPadding={!selectedAlert}
      headerContent={
        <AlertsHistorySlideOverHeader>
          <SlideOverHeaderTitle>
            <PerformanceAlerts24 height={28} width={28} />
            Alert history
          </SlideOverHeaderTitle>

          <AlertCampaign
            name={campaignName}
            campaignId={campaignId}
            templateId={templateId}
            messageMedium={messageMedium}
            campaignType={campaignType}
            campaignState={campaignState}
          />
        </AlertsHistorySlideOverHeader>
      }
      bodyContent={
        <AlertHistory
          campaignId={campaignId}
          setSelectedAlert={setSelectedAlert}
          selectedAlert={selectedAlert}
          campaignAlertsEnabled={campaignAlertsEnabled}
        />
      }
      footerContent={
        buttonContent?.isShown ? <AlertHistoryFooter buttonContent={buttonContent} /> : null
      }
    />
  );
};
